import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function AttendanceRegularization({ user, onLogout }) {
  const [myRequests, setMyRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('my-requests');
  
  const [formData, setFormData] = useState({
    date: '',
    request_type: 'missing_checkin',
    proposed_check_in_time: '',
    proposed_check_out_time: '',
    reason: '',
    proof_document_url: ''
  });
  const [selectedProofFile, setSelectedProofFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);

  useEffect(() => {
    if (activeTab === 'my-requests') {
      fetchMyRequests();
    } else {
      fetchPendingRequests();
    }
  }, [activeTab]);

  const fetchMyRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/attendance-regularization/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching my requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/attendance-regularization/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProofUpload = async () => {
    if (!selectedProofFile) return null;

    setUploadingProof(true);
    try {
      const token = localStorage.getItem('token');
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedProofFile);

      const response = await axios.post(
        `${API}/api/file-uploads/proof`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return response.data.file_url;
    } catch (error) {
      console.error('Error uploading proof:', error);
      throw new Error('Failed to upload proof document');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Upload proof if file is selected
      let proofUrl = formData.proof_document_url;
      if (selectedProofFile) {
        proofUrl = await handleProofUpload();
      }

      await axios.post(
        `${API}/api/attendance-regularization/request`,
        { ...formData, proof_document_url: proofUrl },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      alert('Regularization request submitted successfully!');
      resetForm();
      fetchMyRequests();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Approve this regularization request?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/attendance-regularization/${requestId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      alert('Regularization approved successfully!');
      fetchPendingRequests();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReject = async (requestId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/attendance-regularization/${requestId}/reject?rejection_reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      alert('Regularization rejected!');
      fetchPendingRequests();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      date: '',
      request_type: 'missing_checkin',
      proposed_check_in_time: '',
      proposed_check_out_time: '',
      reason: '',
      proof_document_url: ''
    });
    setSelectedProofFile(null);
    setShowForm(false);
  };

  const handleProofFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size should be less than 10MB');
        return;
      }
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPG, PNG, WEBP and PDF files are allowed');
        return;
      }
      setSelectedProofFile(file);
      // Clear manual URL if file is selected
      setFormData({...formData, proof_document_url: ''});
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const requestTypeLabels = {
    'missing_checkin': 'Missing Check-in',
    'missing_checkout': 'Missing Check-out',
    'full_day_missing': 'Full Day Missing',
    'wrong_time': 'Wrong Time'
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Attendance Regularization</h1>
            <p className="mt-2 text-gray-600">Request corrections for attendance records</p>
          </div>
          {user?.role === 'Employee' && (
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition"
            >
              + Request Regularization
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('my-requests')}
              className={`pb-4 px-2 font-medium transition ${
                activeTab === 'my-requests'
                  ? 'border-b-2 border-[#2b3380] text-[#2b3380]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              My Requests
            </button>
            {(user?.role === 'Admin' || user?.role === 'HR Manager' || user?.role === 'Manager') && (
              <button
                onClick={() => setActiveTab('pending')}
                className={`pb-4 px-2 font-medium transition ${
                  activeTab === 'pending'
                    ? 'border-b-2 border-[#2b3380] text-[#2b3380]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Pending Approvals {pendingRequests.length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                    {pendingRequests.length}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* My Requests Tab */}
        {activeTab === 'my-requests' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : myRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="mb-4">No regularization requests yet</p>
                <button
                  onClick={() => setShowForm(true)}
                  className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition"
                >
                  Create Request
                </button>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proposed Times</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {myRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {new Date(request.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {requestTypeLabels[request.request_type]}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {request.proposed_check_in_time && (
                          <div>In: {new Date(request.proposed_check_in_time).toLocaleTimeString()}</div>
                        )}
                        {request.proposed_check_out_time && (
                          <div>Out: {new Date(request.proposed_check_out_time).toLocaleTimeString()}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {request.reason}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.status)}`}>
                          {request.status}
                        </span>
                        {request.status === 'Rejected' && request.rejection_reason && (
                          <div className="text-xs text-red-600 mt-1">{request.rejection_reason}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Pending Approvals Tab */}
        {activeTab === 'pending' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : pendingRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No pending regularization requests
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proposed Times</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{request.employee_name}</div>
                        <div className="text-xs text-gray-500">{request.employee_code}</div>
                        <div className="text-xs text-gray-500">{request.departments?.join(', ')}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(request.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {requestTypeLabels[request.request_type]}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {request.proposed_check_in_time && (
                          <div>In: {new Date(request.proposed_check_in_time).toLocaleTimeString()}</div>
                        )}
                        {request.proposed_check_out_time && (
                          <div>Out: {new Date(request.proposed_check_out_time).toLocaleTimeString()}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        {request.reason}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        <button
                          onClick={() => handleApprove(request.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(request.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Request Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Request Regularization</h2>
                  <button
                    onClick={resetForm}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.date}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Request Type *
                    </label>
                    <select
                      required
                      value={formData.request_type}
                      onChange={(e) => setFormData({...formData, request_type: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    >
                      <option value="missing_checkin">Missing Check-in</option>
                      <option value="missing_checkout">Missing Check-out</option>
                      <option value="full_day_missing">Full Day Missing</option>
                      <option value="wrong_time">Wrong Time</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposed Check-in Time
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.proposed_check_in_time}
                        onChange={(e) => setFormData({...formData, proposed_check_in_time: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposed Check-out Time
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.proposed_check_out_time}
                        onChange={(e) => setFormData({...formData, proposed_check_out_time: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason *
                    </label>
                    <textarea
                      required
                      rows="3"
                      value={formData.reason}
                      onChange={(e) => setFormData({...formData, reason: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="Explain why regularization is needed..."
                    />
                  </div>

                  {/* Proof Document Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Proof Document (optional)</label>
                    
                    {/* File Upload Section */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#2b3380] transition mb-2">
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                        onChange={handleProofFileSelect}
                        className="hidden"
                        id="proof-upload"
                      />
                      <label htmlFor="proof-upload" className="cursor-pointer">
                        <div className="text-4xl mb-1">📎</div>
                        <p className="text-sm font-medium text-gray-700">Upload proof document</p>
                        <p className="text-xs text-gray-500">JPG, PNG, WEBP or PDF (max 10MB)</p>
                      </label>
                    </div>

                    {selectedProofFile && (
                      <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-xl mr-2">✅</span>
                          <div>
                            <p className="text-sm font-medium text-green-800">{selectedProofFile.name}</p>
                            <p className="text-xs text-green-600">{(selectedProofFile.size / 1024).toFixed(2)} KB</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedProofFile(null)}
                          className="text-red-600 hover:text-red-800 text-sm font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">Medical certificate, email screenshot, or other supporting documents</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || uploadingProof}
                      className="flex-1 px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
                    >
                      {uploadingProof ? 'Uploading...' : loading ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default AttendanceRegularization;
